import { useCallback, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import { logBusinessAuditEvent } from '../../../shell/audit/auditLogs'
import { insertExternalServiceOperation } from '../../../shell/externalServiceRepo'
import type { ExternalServicePaymentMethod, ExternalServiceType } from '../../../shell/externalServiceRepo';

export function usePosRecarga(options: {
  canEdit: boolean;
  currentBranchId: string | null | undefined;
  activeCashSessionId: string | null;
  serviceType: ExternalServiceType;
  amount: string;
  reference: string;
  paymentMethod: ExternalServicePaymentMethod;
  closeRecarga: () => void;
}) {
  const {
    canEdit,
    currentBranchId,
    activeCashSessionId,
    serviceType,
    amount,
    reference,
    paymentMethod,
    closeRecarga,
  } = options;
  const [saving, setSaving] = useState(false);

  const saveRecarga = useCallback(async () => {
    if (!canEdit) {
      showInfo('Modo solo lectura', 'Podés mirar el POS, no cobrar recargas.');
      return;
    }
    if (saving) return;
    if (!currentBranchId) {
      showError('Sucursal', 'Elegí una sucursal para registrar la recarga.');
      return;
    }

    const parsed = parseDecimalInput(amount, { min: 0 });
    if (parsed === null || parsed <= 0) {
      showError('Monto inválido', 'Ingresá un monto válido mayor a cero (ej. 1500 o 1500,50).');
      return;
    }

    if (paymentMethod === 'cash' && !activeCashSessionId) {
      showError('Caja cerrada', 'Para cobrar una recarga en efectivo, abrí la caja.');
      return;
    }

    setSaving(true);
    try {
      await insertExternalServiceOperation({
        branchId: currentBranchId,
        type: serviceType,
        amount: parsed,
        reference: reference.trim() || null,
        paymentMethod,
        cashSessionId: activeCashSessionId,
      });
      closeRecarga();
      showSuccess('Recarga registrada');
      void logBusinessAuditEvent('pos.external_service.create', {
        branchId: currentBranchId,
        type: serviceType,
        amount: parsed,
        paymentMethod,
        cashSessionId: activeCashSessionId,
      }).catch(captureCatch('pos', 'external-service-audit'));
    } catch (error: any) {
      presentUnexpectedFailure({ module: 'pos', op: 'error', error: error, title: 'Error', userMessage: 'No se pudo registrar la recarga.' });
    } finally {
      setSaving(false);
    }
  }, [
    activeCashSessionId,
    amount,
    canEdit,
    closeRecarga,
    currentBranchId,
    paymentMethod,
    reference,
    saving,
    serviceType,
  ]);

  return { saving, saveRecarga };
}
